/**
 * CPU main-deck lists for vs AI. Picked at random each duel.
 *
 * Sources: Pojo Critter Format (July 2002), Pojo's Metal Raiders meta notes,
 * NickWhiz1's June 2002 Exodia preview, the July 2002 Pojo format thread,
 * and the 29 July 2002 tournament report. Ratios follow that July list.
 * Sangan searches ATK 1500 or less. Witch searches DEF 1500 or less.
 * Missing catalog IDs are skipped when the deck is built.
 *
 * Mains are already inside the house banlist. `substitutes` fills a slot
 * if a later list cuts another copy, so the hole does not become the
 * highest-ATK level 4 in the catalog. Easy / Normal / Hard still also
 * sit on `ERA_LIMITS`. The player's list is checked against the same
 * caps when the duel starts.
 */

import { houseBanlistActive, houseLimits } from './deckValidation'

export type AiRecipeId =
  | 'walls'
  | 'burn'
  | 'weaks'
  | 'flips'
  | 'ox'
  | 'heal'
  | 'swarm'
  | 'fire'
  | 'harpie'
  | 'moth'
  | 'yugi'
  | 'gaia'
  | 'beast'
  | 'water'
  | 'wind'
  | 'zombie'
  | 'guard'
  | 'machine'
  | 'gate'
  | 'direct'
  | 'fish'
  | 'skull'
  | 'dragon'
  | 'stallburn'
  | 'hats'
  | 'coins'
  | 'turtle'
  | 'fiend'
  | 'warrior'
  | 'earth'
  | 'critter'
  | 'exodia'
  | 'muka'
  | 'soul'
  | 'fusion'
  | 'lord'
  | 'mill'
  | 'toolbox'
  | 'beat'
  | 'dark'
  | 'critop'
  | 'mukare'
  | 'exobeat'
  | 'soulx'
  | 'hatwill'
  | 'fishre'
  | 'flute'
  | 'will'
  | 'goblin'

export const EXODIA_PIECE_IDS = [
  '33396948', // Exodia the Forbidden One
  '8124921', // Right Leg
  '44519536', // Left Leg
  '70903634', // Right Arm
  '7902349', // Left Arm
] as const

export const EXODIA_PIECE_SET = new Set<string>(EXODIA_PIECE_IDS)

export const POLYMERIZATION_ID = '24094653'

export type AiDifficulty = 'easy' | 'normal' | 'hard' | 'expert' | 'master'

/**
 * July 2002 community list. Applied on Easy / Normal / Hard, on top of
 * the house banlist when one is active.
 */
export const ERA_LIMITS: Record<string, number> = {
  '55144522': 1, // Pot of Greed
  '83764718': 1, // Monster Reborn
  '53129443': 1, // Dark Hole
  '4031928': 1, // Change of Heart
  '12580477': 1, // Raigeki
  '44095762': 1, // Mirror Force
  '72302403': 2, // Swords of Revealing Light
  '72892473': 2, // Card Destruction
  '19613556': 2, // Heavy Storm
  '26202165': 2, // Sangan
  '78010363': 2, // Witch of the Black Forest
}

/**
 * Always-on CPU caps. Trap Hole at 2–3 made almost every list open on it.
 * Applies on every difficulty, including Expert / Master.
 */
export const CPU_COPY_CAPS: Record<string, number> = {
  '4206964': 1, // Trap Hole
}

export type RecipeLine = { id: string; copies: number }

export interface AiRecipeTheme {
  race?: string
  attribute?: string
}

export interface AiRecipe {
  id: AiRecipeId
  /** Full-power 40-card list. Expert / Master start from these counts. */
  main: RecipeLine[]
  /** Fills slots cut by a copy cap. */
  substitutes?: RecipeLine[]
  /** Pure-type or pure-attribute lists. Spells / traps are unrestricted. */
  theme?: AiRecipeTheme
}

const line = (id: string, copies: number): RecipeLine => ({ id, copies })

/** Spells and flips the planner already scores. Used when a cap opens a slot. */
const SUB_GENERIC: RecipeLine[] = [
  line('66788016', 2), // Fissure
  line('54652250', 2), // Man-Eater Bug
  line('44209392', 2), // Castle Walls
  line('7089711', 2), // Hane-Hane
]

const SUB_FIRE: RecipeLine[] = [
  line('66788016', 2), // Fissure
  line('44209392', 2), // Castle Walls
  line('46918794', 1), // Tremendous Fire
  line('60862676', 1), // Flame Cerebrus
]

const SUB_SPELLCASTER: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 2), // Castle Walls
  line('93221206', 1), // Ancient Elf
  line('87557188', 1), // The Stern Mystic
]

const SUB_BEAST: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 2), // Castle Walls
  line('7089711', 1), // Hane-Hane
  line('98818516', 1), // Frenzied Panda
]

const SUB_WATER: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 2), // Castle Walls
  line('37313348', 1), // Turtle Tiger
  line('17535588', 1), // Armored Starfish
]

const SUB_WIND: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 2), // Castle Walls
  line('75356564', 1), // Petit Dragon
  line('98252586', 1), // Follow Wind
]

const SUB_ZOMBIE: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 1), // Castle Walls
  line('30778711', 1), // Shadow Ghoul
  line('46474915', 1), // Magical Ghost
]

const SUB_MACHINE: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 2), // Castle Walls
  line('11384280', 1), // Cannon Soldier
  line('8471389', 1), // Giga-Tech Wolf
]

const SUB_FIEND: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 2), // Castle Walls
  line('41392891', 1), // Feral Imp
  line('47060154', 1), // Mystic Clown
]

const SUB_WARRIOR: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 2), // Castle Walls
  line('56342351', 1), // M-Warrior #1
  line('92731455', 1), // M-Warrior #2
]

const SUB_EARTH: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 2), // Castle Walls
  line('90357090', 1), // Silver Fang
  line('13039848', 1), // Giant Soldier of Stone
]

const SUB_DARK: RecipeLine[] = [
  line('66788016', 1), // Fissure
  line('44209392', 2), // Castle Walls
  line('13945283', 1), // Wall of Illusion
  line('41392891', 1), // Feral Imp
]

const WALLS: AiRecipe = {
  id: 'walls',
  main: [
    line('15025844', 3), // Mystical Elf
    line('13039848', 3), // Giant Soldier of Stone
    line('85639257', 3), // Aqua Madoor
    line('51275027', 3), // The Unhappy Maiden
    line('55875323', 3), // Electric Lizard
    line('80770678', 3), // Spirit of the Harp
    line('37313348', 2), // Turtle Tiger
    line('13945283', 2), // Wall of Illusion
    line('72302403', 2), // Swords of Revealing Light
    line('38199696', 2), // Red Medicine
    line('84257639', 1), // Dian Keto
    line('44209392', 3), // Castle Walls
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('17535588', 2), // Armored Starfish
    line('38142739', 2), // Petit Angel
    line('85309439', 2), // Ray & Temperature
    line('20060230', 1), // Hard Armor
  ],
}

const BURN: AiRecipe = {
  id: 'burn',
  main: [
    line('21263083', 3), // Pale Beast
    line('71407486', 2), // Fireyarou
    line('73481154', 2), // Destroyer Golem
    line('96851799', 2), // Hinotama Soul
    line('46130346', 3), // Hinotama
    line('73134081', 3), // Final Flame
    line('19523799', 2), // Ookazi
    line('76103675', 3), // Sparks
    line('46918794', 3), // Tremendous Fire
    line('24068492', 3), // Just Desserts
    line('38199696', 2), // Red Medicine
    line('12607053', 2), // Waboku
    line('44209392', 3), // Castle Walls
    line('37313348', 2), // Turtle Tiger
    line('15025844', 3), // Mystical Elf
    line('80770678', 2), // Spirit of the Harp
  ],
}

const WEAKS: AiRecipe = {
  id: 'weaks',
  main: [
    line('76184692', 3), // Hitotsu-Me Giant
    line('32452818', 3), // Beaver Warrior
    line('90357090', 3), // Silver Fang
    line('21817254', 3), // Mega Thunderball
    line('7805359', 3), // Niwatori
    line('549481', 2), // Prevent Rat
    line('66602787', 2), // Saggi the Dark Clown
    line('68846917', 2), // Rock Ogre Grotto #1
    line('46130346', 2), // Hinotama
    line('38199696', 2), // Red Medicine
    line('76103675', 2), // Sparks
    line('98818516', 2), // Frenzied Panda
    line('75499502', 2), // Master & Expert
    line('9159938', 2), // Dark Gray
    line('83464209', 2), // Mystical Sheep #2
    line('4206964', 1), // Trap Hole
    line('12607053', 2), // Waboku
    line('36121917', 2), // Monster Egg
  ],
}

const FLIPS: AiRecipe = {
  id: 'flips',
  main: [
    line('54652250', 3), // Man-Eater Bug
    line('7089711', 3), // Hane-Hane
    line('31560081', 3), // Magician of Faith
    line('28933734', 3), // Mask of Darkness
    line('51371017', 3), // Princess of Tsurugi
    line('46461247', 2), // Trap Master
    line('9076207', 2), // Armed Ninja
    line('33066139', 2), // Reaper of the Cards
    line('72302403', 2), // Swords of Revealing Light
    line('38199696', 2), // Red Medicine
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('44209392', 3), // Castle Walls
    line('77622396', 2), // Reverse Trap
    line('13945283', 2), // Wall of Illusion
    line('54098121', 2), // Mysterious Puppeteer
    line('20060230', 2), // Hard Armor
    line('38142739', 1), // Petit Angel
  ],
}

const OX: AiRecipe = {
  id: 'ox',
  main: [
    line('5053103', 3), // Battle Ox
    line('24611934', 3), // Ryu-Kishin Powered
    line('1784619', 3), // Uraby
    line('1184620', 3), // Kojikocy
    line('68516705', 2), // Mystic Horseman
    line('98374133', 2), // Invigoration
    line('38199696', 2), // Red Medicine
    line('4206964', 1), // Trap Hole
    line('32452818', 3), // Beaver Warrior
    line('76184692', 3), // Hitotsu-Me Giant
    line('45121025', 3), // Ogre of the Black Shadow
    line('36607978', 3), // Mystical Moon
    line('86318356', 2), // Sogen
    line('12607053', 2), // Waboku
    line('44209392', 2), // Castle Walls
    line('98818516', 2), // Frenzied Panda
    line('24094653', 1), // Polymerization — Rabid Horseman if both materials show up
  ],
}

const HEAL: AiRecipe = {
  id: 'heal',
  main: [
    line('84257639', 3), // Dian Keto
    line('38199696', 3), // Red Medicine
    line('11868825', 3), // Goblin's Secret Remedy
    line('80770678', 3), // Spirit of the Harp
    line('15025844', 3), // Mystical Elf
    line('38142739', 3), // Petit Angel
    line('85309439', 2), // Ray & Temperature
    line('1557499', 2), // Silver Bow and Arrow
    line('85639257', 3), // Aqua Madoor
    line('13039848', 3), // Giant Soldier of Stone
    line('51275027', 3), // The Unhappy Maiden
    line('72302403', 2), // Swords of Revealing Light
    line('44209392', 3), // Castle Walls
    line('12607053', 2), // Waboku
    line('37313348', 2), // Turtle Tiger
  ],
}

const SWARM: AiRecipe = {
  id: 'swarm',
  main: [
    line('80604091', 1), // Ultimate Offering
    line('32274490', 3), // Skull Servant
    line('15303296', 3), // Ryu-Kishin
    line('56342351', 3), // M-Warrior #1
    line('92731455', 3), // M-Warrior #2
    line('36121917', 3), // Monster Egg
    line('53153481', 3), // Armaill
    line('20060230', 2), // Hard Armor
    line('76211194', 3), // Meda Bat
    line('15401633', 3), // Kagemusha of the Blue Flame
    line('12607053', 2), // Waboku
    line('44209392', 3), // Castle Walls
    line('38199696', 2), // Red Medicine
    line('4206964', 1), // Trap Hole
    line('83887306', 2), // Two-Pronged Attack
    line('549481', 3), // Prevent Rat
  ],
}

const FIRE: AiRecipe = {
  id: 'fire',
  theme: { attribute: 'FIRE' },
  main: [
    line('71407486', 3), // Fireyarou
    line('96851799', 3), // Hinotama Soul
    line('34460851', 3), // Flame Manipulator
    line('60862676', 3), // Flame Cerebrus
    line('46130346', 3), // Hinotama
    line('73134081', 3), // Final Flame
    line('19523799', 3), // Ookazi
    line('76103675', 3), // Sparks
    line('46918794', 2), // Tremendous Fire
    line('24068492', 3), // Just Desserts
    line('12607053', 2), // Waboku
    line('44209392', 3), // Castle Walls
    line('38199696', 3), // Red Medicine
    line('84257639', 2), // Dian Keto
    line('77622396', 1), // Reverse Trap
  ],
}

const HARPIE: AiRecipe = {
  id: 'harpie',
  main: [
    line('76812113', 3), // Harpie Lady
    line('12206212', 2), // Harpie Lady Sisters — only via Elegant Egotist
    line('90219263', 3), // Elegant Egotist
    line('50913601', 3), // Mountain
    line('98252586', 3), // Follow Wind
    line('87796900', 3), // Winged Dragon, Guardian of the Fortress #1
    line('55444629', 3), // Lesser Dragon
    line('72842870', 3), // Tyhone
    line('85705804', 3), // Kurama
    line('75356564', 3), // Petit Dragon
    line('12607053', 2), // Waboku
    line('44209392', 3), // Castle Walls
    line('38199696', 2), // Red Medicine
    line('76103675', 2), // Sparks
    line('4206964', 1), // Trap Hole
    line('72302403', 1), // Swords of Revealing Light
  ],
}

const MOTH: AiRecipe = {
  id: 'moth',
  main: [
    line('58192742', 3), // Petit Moth
    line('40240595', 3), // Cocoon of Evolution
    line('87756343', 3), // Larvae Moth
    line('14141448', 1), // Great Moth
    line('77007920', 3), // Laser Cannon Armor
    line('87430998', 2), // Forest
    line('94675535', 3), // Larvas
    line('56283725', 3), // Kumootoko
    line('89091579', 2), // Basic Insect
    line('12607053', 2), // Waboku
    line('44209392', 3), // Castle Walls
    line('38199696', 2), // Red Medicine
    line('72302403', 2), // Swords of Revealing Light
    line('4206964', 1), // Trap Hole
    line('85639257', 3), // Aqua Madoor
    line('15025844', 2), // Mystical Elf
    line('37313348', 2), // Turtle Tiger
  ],
}

const YUGI: AiRecipe = {
  id: 'yugi',
  theme: { race: 'Spellcaster' },
  main: [
    line('36996508', 2), // Dark Magician
    line('50930991', 3), // Neo the Magic Swordsman
    line('91939608', 2), // Rogue Doll
    line('87557188', 2), // The Stern Mystic
    line('93221206', 2), // Ancient Elf
    line('31560081', 3), // Magician of Faith
    line('15025844', 3), // Mystical Elf
    line('85639257', 2), // Aqua Madoor
    line('59197169', 2), // Yami
    line('91595718', 2), // Book of Secret Arts
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('72302403', 2), // Swords of Revealing Light
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('66788016', 2), // Fissure
    line('78010363', 2), // Witch of the Black Forest
    line('19613556', 1), // Heavy Storm
    line('90963488', 2), // Nemuriko
  ],
}

const GAIA: AiRecipe = {
  id: 'gaia',
  main: [
    line('6368038', 3), // Gaia The Fierce Knight
    line('28279543', 3), // Curse of Dragon
    line('24094653', 3), // Polymerization
    line('44287299', 3), // Masaki the Legendary Swordsman
    line('34460851', 2), // Flame Manipulator
    line('91152256', 3), // Celtic Guardian
    line('4206964', 1), // Trap Hole
    line('12607053', 2), // Waboku
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('61854111', 2), // Legendary Sword
    line('17814387', 2), // Reinforcements
    line('1184620', 2), // Kojikocy
    line('81057959', 2), // D. Human
    line('19613556', 1), // Heavy Storm
    line('44095762', 1), // Mirror Force
    line('5053103', 2), // Battle Ox
  ],
}

const BEAST: AiRecipe = {
  id: 'beast',
  theme: { race: 'Beast' },
  main: [
    line('21263083', 3), // Pale Beast
    line('6285791', 3), // The Wicked Worm Beast
    line('68516705', 3), // Mystic Horseman
    line('90357090', 3), // Silver Fang
    line('98818516', 2), // Frenzied Panda
    line('75499502', 2), // Master & Expert
    line('7089711', 2), // Hane-Hane
    line('87430998', 2), // Forest
    line('46009906', 3), // Beast Fangs
    line('24094653', 2), // Polymerization — Rabid Horseman
    line('5053103', 2), // Battle Ox
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('72302403', 1), // Swords of Revealing Light
    line('9159938', 2), // Dark Gray
    line('94675535', 2), // Larvas
    line('66788016', 2), // Fissure
    line('19613556', 1), // Heavy Storm
  ],
}

const WATER: AiRecipe = {
  id: 'water',
  theme: { attribute: 'WATER' },
  main: [
    line('23771716', 3), // 7 Colored Fish
    line('13429800', 3), // Great White
    line('33178416', 2), // Misairuzame
    line('85326399', 2), // Spike Seadra
    line('75376965', 2), // Enchanting Mermaid
    line('37313348', 2), // Turtle Tiger
    line('39004808', 2), // Root Water
    line('17535588', 2), // Armored Starfish
    line('85639257', 2), // Aqua Madoor
    line('22702055', 2), // Umi
    line('77027445', 3), // Power of Kaishin
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('72302403', 1), // Swords of Revealing Light
    line('66788016', 2), // Fissure
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('19613556', 1), // Heavy Storm
    line('78010363', 2), // Witch — finds the Fish (DEF 800)
    line('31560081', 2), // Magician of Faith
  ],
}

const WIND: AiRecipe = {
  id: 'wind',
  theme: { attribute: 'WIND' },
  main: [
    line('76812113', 3), // Harpie Lady
    line('90219263', 2), // Elegant Egotist
    line('12206212', 1), // Harpie Lady Sisters
    line('10202894', 2), // Skull Red Bird
    line('87796900', 2), // Winged Dragon #1
    line('55444629', 2), // Lesser Dragon
    line('72842870', 2), // Tyhone
    line('85705804', 2), // Kurama
    line('50913601', 2), // Mountain
    line('98252586', 2), // Follow Wind
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('72302403', 2), // Swords of Revealing Light
    line('66788016', 2), // Fissure
    line('53129443', 1), // Dark Hole
    line('12580477', 1), // Raigeki
    line('19613556', 1), // Heavy Storm
    line('75356564', 2), // Petit Dragon
    line('33064647', 2), // One-Eyed Shield Dragon
    line('1435851', 2), // Dragon Treasure
    line('24094653', 2), // Polymerization
  ],
}

const ZOMBIE: AiRecipe = {
  id: 'zombie',
  theme: { race: 'Zombie' },
  main: [
    line('29155212', 3), // Pumpking the King of Ghosts
    line('62121', 2), // Castle of Dark Illusions
    line('66672569', 3), // Dragon Zombie
    line('46474915', 2), // Magical Ghost
    line('32864', 2), // The 13th Grave
    line('32274490', 3), // Skull Servant
    line('23424603', 2), // Wasteland
    line('15052462', 2), // Violet Crystal
    line('83764718', 1), // Monster Reborn
    line('55144522', 1), // Pot of Greed
    line('72302403', 2), // Swords of Revealing Light
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('66788016', 2), // Fissure
    line('53129443', 1), // Dark Hole
    line('44209392', 3), // Castle Walls
    line('38199696', 2), // Red Medicine
    line('12580477', 1), // Raigeki
    line('19613556', 1), // Heavy Storm
    line('30778711', 2), // Shadow Ghoul
    line('41949033', 2), // Dark Assailant
  ],
}

const GUARD: AiRecipe = {
  id: 'guard',
  main: [
    line('40453765', 3), // Swamp Battleguard
    line('20394040', 3), // Lava Battleguard
    line('91152256', 3), // Celtic Guardian
    line('86318356', 2), // Sogen
    line('61854111', 2), // Legendary Sword
    line('17814387', 2), // Reinforcements
    line('5053103', 2), // Battle Ox
    line('1184620', 2), // Kojikocy
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('19613556', 1), // Heavy Storm
    line('44095762', 1), // Mirror Force
    line('4031928', 1), // Change of Heart
    line('6368038', 2), // Gaia The Fierce Knight
    line('81057959', 2), // D. Human
    line('24094653', 2), // Polymerization
    line('28279543', 1), // Curse of Dragon
  ],
}

const MACHINE: AiRecipe = {
  id: 'machine',
  theme: { race: 'Machine' },
  main: [
    line('87322377', 3), // Launcher Spider
    line('29172562', 3), // Steel Ogre Grotto #1
    line('73481154', 3), // Destroyer Golem
    line('25769732', 2), // Machine Conversion Factory
    line('55444629', 2), // Lesser Dragon — Metal Dragon material
    line('24094653', 2), // Polymerization
    line('8471389', 2), // Giga-Tech Wolf
    line('11384280', 2), // Cannon Soldier — Labyrinth Tank material
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('66788016', 2), // Fissure
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('72302403', 2), // Swords of Revealing Light
    line('19613556', 1), // Heavy Storm
    line('4031928', 1), // Change of Heart
    line('68005187', 2), // Soul Exchange
    line('13039848', 2), // Giant Soldier of Stone
    line('13945283', 2), // Wall of Illusion
    line('17814387', 2), // Reinforcements
  ],
}

const GATE: AiRecipe = {
  id: 'gate',
  main: [
    line('25955164', 2), // Sanga of the Thunder
    line('62340868', 2), // Kazejin
    line('98434877', 2), // Suijin
    line('25833572', 1), // Gate Guardian
    line('68005187', 2), // Soul Exchange
    line('13039848', 3), // Giant Soldier of Stone
    line('13945283', 2), // Wall of Illusion
    line('97590747', 2), // La Jinn
    line('5053103', 2), // Battle Ox
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('4031928', 1), // Change of Heart
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('19613556', 1), // Heavy Storm
    line('44095762', 1), // Mirror Force
    line('54652250', 2), // Man-Eater Bug
    line('31560081', 2), // Magician of Faith
    line('80604091', 1), // Ultimate Offering
    line('17814387', 2), // Reinforcements
    line('15025844', 1), // Mystical Elf
  ],
}

const DIRECT: AiRecipe = {
  id: 'direct',
  main: [
    line('98049915', 2), // Mystic Lamp
    line('32809211', 2), // Jinzo #7
    line('12472242', 2), // Leghul
    line('58861941', 2), // Ooguchi
    line('5901497', 2), // Queen's Double
    line('21347810', 2), // Rainbow Flower
    line('97590747', 3), // La Jinn
    line('23771716', 2), // 7 Colored Fish
    line('54652250', 2), // Man-Eater Bug
    line('31560081', 2), // Magician of Faith
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('19613556', 1), // Heavy Storm
    line('4031928', 1), // Change of Heart
    line('13039848', 2), // Giant Soldier of Stone
    line('13945283', 2), // Wall of Illusion
    line('17814387', 2), // Reinforcements
  ],
}

const FISH: AiRecipe = {
  id: 'fish',
  main: [
    line('23771716', 3), // 7 Colored Fish
    line('97590747', 3), // La Jinn
    line('5053103', 3), // Battle Ox
    line('78010363', 2), // Witch
    line('26202165', 2), // Sangan
    line('31560081', 3), // Magician of Faith
    line('54652250', 2), // Man-Eater Bug
    line('13945283', 2), // Wall of Illusion
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 3), // Fissure
    line('79759861', 2), // Tribute to the Doomed
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('17814387', 2), // Reinforcements
    line('13039848', 1), // Giant Soldier of Stone
  ],
}

const SKULL: AiRecipe = {
  id: 'skull',
  main: [
    line('70781052', 2), // Summoned Skull
    line('68005187', 2), // Soul Exchange
    line('5053103', 2), // Battle Ox
    line('97590747', 3), // La Jinn
    line('78010363', 2), // Witch — DEF 1200 finds Skull
    line('26202165', 2), // Sangan
    line('31560081', 3), // Magician of Faith
    line('54652250', 2), // Man-Eater Bug
    line('13945283', 2), // Wall of Illusion
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('66788016', 2), // Fissure
    line('19613556', 1), // Heavy Storm
    line('72302403', 2), // Swords of Revealing Light
    line('4206964', 1), // Trap Hole
    line('12607053', 2), // Waboku
    line('44095762', 1), // Mirror Force
    line('13039848', 2), // Giant Soldier of Stone
    line('79759861', 2), // Tribute to the Doomed
    line('56830749', 1), // Share the Pain
    line('74677422', 1), // Red-Eyes Black Dragon
  ],
}

const DRAGON: AiRecipe = {
  id: 'dragon',
  main: [
    line('89631139', 3), // Blue-Eyes White Dragon
    line('17985575', 2), // Lord of D.
    line('43973174', 2), // The Flute of Summoning Dragon
    line('31560081', 1), // Magician of Faith
    line('74677422', 2), // Red-Eyes Black Dragon
    line('55444629', 2), // Lesser Dragon
    line('88819587', 2), // Baby Dragon
    line('50913601', 2), // Mountain
    line('1435851', 2), // Dragon Treasure
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('17814387', 2), // Reinforcements
    line('54652250', 2), // Man-Eater Bug
    line('13039848', 2), // Giant Soldier of Stone
    line('68005187', 2), // Soul Exchange
  ],
}

const STALLBURN: AiRecipe = {
  id: 'stallburn',
  main: [
    line('85639257', 3), // Aqua Madoor
    line('31122090', 2), // Gyakutenno Megami
    line('44209392', 3), // Castle Walls
    line('24068492', 2), // Just Desserts
    line('19523799', 2), // Ookazi
    line('73134081', 2), // Final Flame
    line('46918794', 2), // Tremendous Fire
    line('13039848', 3), // Giant Soldier of Stone
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('72302403', 2), // Swords of Revealing Light
    line('13945283', 2), // Wall of Illusion
    line('51275027', 3), // The Unhappy Maiden
    line('12580477', 1), // Raigeki
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('53129443', 1), // Dark Hole
    line('15025844', 2), // Mystical Elf
    line('44095762', 1), // Mirror Force
    line('88279736', 2), // Robbin' Goblin
    line('19613556', 1), // Heavy Storm
    line('38199696', 1), // Red Medicine
  ],
}

const HATS: AiRecipe = {
  id: 'hats',
  main: [
    line('15150365', 3), // White Magical Hat
    line('10189126', 3), // Masked Sorcerer
    line('77414722', 1), // Magic Jammer
    line('88279736', 2), // Robbin' Goblin
    line('97590747', 3), // La Jinn
    line('78010363', 2), // Witch
    line('26202165', 2), // Sangan
    line('31560081', 2), // Magician of Faith
    line('54652250', 2), // Man-Eater Bug
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('79759861', 2), // Tribute to the Doomed
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('13945283', 2), // Wall of Illusion
    line('13039848', 2), // Giant Soldier of Stone
  ],
}

const COINS: AiRecipe = {
  id: 'coins',
  main: [
    line('71625222', 3), // Time Wizard
    line('81480460', 2), // Barrel Dragon
    line('79759861', 1), // Tribute to the Doomed
    line('94773007', 2), // Jirai Gumo
    line('88819587', 2), // Baby Dragon — Thousand Dragon
    line('24094653', 2), // Polymerization
    line('68005187', 2), // Soul Exchange
    line('97590747', 2), // La Jinn
    line('5053103', 2), // Battle Ox
    line('31560081', 2), // Magician of Faith
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('13039848', 2), // Giant Soldier of Stone
    line('13945283', 2), // Wall of Illusion
    line('54652250', 2), // Man-Eater Bug
  ],
}

const TURTLE: AiRecipe = {
  id: 'turtle',
  main: [
    line('95727991', 3), // Catapult Turtle
    line('11384280', 3), // Cannon Soldier
    line('79759861', 1), // Tribute to the Doomed
    line('89631139', 2), // Blue-Eyes White Dragon
    line('70781052', 2), // Summoned Skull
    line('74677422', 2), // Red-Eyes Black Dragon
    line('26202165', 2), // Sangan — ATK 1000, Turtle can search it
    line('78010363', 2), // Witch — finds Cannon Soldier
    line('68005187', 2), // Soul Exchange
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('13039848', 2), // Giant Soldier of Stone
    line('97590747', 2), // La Jinn
    line('31560081', 2), // Magician of Faith
    line('80604091', 1), // Ultimate Offering
  ],
}

const FIEND: AiRecipe = {
  id: 'fiend',
  theme: { race: 'Fiend' },
  main: [
    line('97590747', 3), // La Jinn
    line('70781052', 2), // Summoned Skull
    line('26202165', 2), // Sangan
    line('24611934', 2), // Ryu-Kishin Powered
    line('13723605', 2), // Man-Eating Treasure Chest
    line('86325596', 2), // Baron of the Fiend Sword
    line('47060154', 2), // Mystic Clown
    line('13945283', 2), // Wall of Illusion
    line('41392891', 2), // Feral Imp
    line('59197169', 2), // Yami
    line('4614116', 2), // Dark Energy
    line('37120512', 2), // Sword of Dark Destruction
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('66788016', 2), // Fissure
    line('19613556', 1), // Heavy Storm
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('31560081', 2), // Magician of Faith
  ],
}

const WARRIOR: AiRecipe = {
  id: 'warrior',
  theme: { race: 'Warrior' },
  main: [
    line('6368038', 2), // Gaia The Fierce Knight
    line('40453765', 2), // Swamp Battleguard
    line('20394040', 2), // Lava Battleguard
    line('30113682', 2), // Judge Man
    line('91152256', 3), // Celtic Guardian
    line('1184620', 2), // Kojikocy
    line('44287299', 2), // Masaki
    line('86318356', 2), // Sogen
    line('61854111', 2), // Legendary Sword
    line('17814387', 2), // Reinforcements
    line('24094653', 2), // Polymerization
    line('56342351', 2), // M-Warrior #1
    line('92731455', 2), // M-Warrior #2
    line('28279543', 2), // Curse of Dragon
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('66788016', 2), // Fissure
    line('19613556', 1), // Heavy Storm
    line('72302403', 1), // Swords of Revealing Light
  ],
}

const EARTH: AiRecipe = {
  id: 'earth',
  theme: { attribute: 'EARTH' },
  main: [
    line('6368038', 2), // Gaia The Fierce Knight
    line('40453765', 2), // Swamp Battleguard
    line('20394040', 2), // Lava Battleguard
    line('5053103', 3), // Battle Ox
    line('1784619', 2), // Uraby
    line('1184620', 2), // Kojikocy
    line('73481154', 2), // Destroyer Golem
    line('98374133', 2), // Invigoration
    line('86318356', 2), // Sogen
    line('24094653', 2), // Polymerization
    line('90357090', 2), // Silver Fang
    line('68516705', 2), // Mystic Horseman
    line('28279543', 2), // Curse of Dragon
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('66788016', 2), // Fissure
    line('19613556', 1), // Heavy Storm
    line('13039848', 2), // Giant Soldier of Stone
    line('72302403', 1), // Swords of Revealing Light
  ],
}

/** Baneful's Critter shell, July 2002 counts. Witch finds La Jinn; Sangan does not. */
const CRITTER: AiRecipe = {
  id: 'critter',
  main: [
    line('26202165', 2), // Sangan
    line('78010363', 2), // Witch of the Black Forest
    line('97590747', 3), // La Jinn
    line('31560081', 3), // Magician of Faith
    line('54652250', 2), // Man-Eater Bug
    line('40640057', 2), // Kuriboh
    line('28933734', 1), // Mask of Darkness
    line('7089711', 1), // Hane-Hane
    line('13945283', 2), // Wall of Illusion
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('79759861', 1), // Tribute to the Doomed
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('4206964', 1), // Trap Hole
    line('12607053', 2), // Waboku
    line('77414722', 2), // Magic Jammer
    line('3819470', 2), // Seven Tools of the Bandit
    line('41420027', 1), // Solemn Judgment
    line('98069388', 1), // Horn of Heaven
    line('13039848', 1), // Giant Soldier of Stone
  ],
}

/** NickWhiz1, June 2002: Cannon Soldier tributes Sangan or Witch. */
const EXODIA: AiRecipe = {
  id: 'exodia',
  main: [
    line('33396948', 1),
    line('8124921', 1),
    line('44519536', 1),
    line('70903634', 1),
    line('7902349', 1),
    line('26202165', 2), // Sangan
    line('78010363', 2), // Witch
    line('11384280', 2), // Cannon Soldier
    line('40640057', 2), // Kuriboh
    line('31560081', 2), // Magician of Faith
    line('15025844', 2), // Mystical Elf
    line('13039848', 2), // Giant Soldier of Stone
    line('85639257', 2), // Aqua Madoor
    line('13945283', 2), // Wall of Illusion
    line('85602018', 2), // Last Will
    line('80604091', 1), // Ultimate Offering
    line('55144522', 1), // Pot of Greed
    line('66788016', 1), // Fissure
    line('19613556', 1), // Heavy Storm
    line('72302403', 2), // Swords of Revealing Light
    line('83764718', 1), // Monster Reborn
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('77414722', 1), // Magic Jammer
    line('3819470', 1), // Seven Tools of the Bandit
    line('4206964', 1), // Trap Hole
  ],
}

const MUKA: AiRecipe = {
  id: 'muka',
  main: [
    line('46657337', 3), // Muka Muka
    line('31786629', 3), // Thunder Dragon
    line('97590747', 2), // La Jinn
    line('78010363', 2), // Witch — DEF 1500 finds Thunder Dragon
    line('26202165', 2), // Sangan — ATK 1500 finds Muka
    line('31560081', 3), // Magician of Faith
    line('40640057', 1), // Kuriboh
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('79759861', 2), // Tribute to the Doomed
    line('77414722', 2), // Magic Jammer — Thunder Dragon is the discard
    line('44095762', 1), // Mirror Force
    line('17814387', 2), // Reinforcements
    line('4206964', 1), // Trap Hole
    line('12607053', 1), // Waboku
    line('72302403', 1), // Swords of Revealing Light
    line('24094653', 2), // Polymerization — Twin-Headed Thunder Dragon
    line('54652250', 1), // Man-Eater Bug
    line('28933734', 1), // Mask of Darkness
    line('41420027', 1), // Solemn Judgment
    line('3819470', 1), // Seven Tools of the Bandit
  ],
}

const SOUL: AiRecipe = {
  id: 'soul',
  main: [
    line('70781052', 2), // Summoned Skull
    line('68005187', 2), // Soul Exchange
    line('56830749', 3), // Share the Pain
    line('79759861', 1), // Tribute to the Doomed
    line('78010363', 2), // Witch
    line('26202165', 2), // Sangan
    line('31560081', 3), // Magician of Faith
    line('10189126', 2), // Masked Sorcerer
    line('15150365', 2), // White Magical Hat
    line('54652250', 1), // Man-Eater Bug
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 3), // Fissure
    line('44095762', 1), // Mirror Force
    line('41420027', 2), // Solemn Judgment
    line('4206964', 1), // Trap Hole
    line('12607053', 2), // Waboku
    line('77414722', 1), // Magic Jammer
    line('98069388', 1), // Horn of Heaven
    line('97590747', 2), // La Jinn
    line('72302403', 1), // Swords of Revealing Light
  ],
}

const FUSION: AiRecipe = {
  id: 'fusion',
  main: [
    line('24094653', 3), // Polymerization
    line('31786629', 3), // Thunder Dragon — Twin-Headed Thunder Dragon
    line('31560081', 1), // Magician of Faith
    line('70781052', 2), // Summoned Skull — Black Skull Dragon
    line('74677422', 2), // Red-Eyes Black Dragon
    line('6368038', 2), // Gaia The Fierce Knight
    line('28279543', 2), // Curse of Dragon
    line('71625222', 2), // Time Wizard — Thousand Dragon
    line('88819587', 2), // Baby Dragon
    line('5053103', 2), // Battle Ox — Rabid Horseman
    line('68516705', 2), // Mystic Horseman
    line('11384280', 2), // Cannon Soldier — Labyrinth Tank
    line('8471389', 1), // Giga-Tech Wolf
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('44095762', 1), // Mirror Force
    line('19613556', 1), // Heavy Storm
    line('78010363', 2), // Witch
    line('66788016', 2), // Fissure
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
  ],
}

const LORD: AiRecipe = {
  id: 'lord',
  main: [
    line('89631139', 3), // Blue-Eyes White Dragon
    line('17985575', 3), // Lord of D.
    line('43973174', 2), // The Flute of Summoning Dragon
    line('79759861', 1), // Tribute to the Doomed
    line('74677422', 2), // Red-Eyes Black Dragon
    line('88819587', 2), // Baby Dragon
    line('50913601', 2), // Mountain
    line('26202165', 2), // Sangan
    line('78010363', 1), // Witch
    line('31560081', 2), // Magician of Faith
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('77414722', 1), // Magic Jammer
    line('3819470', 1), // Seven Tools of the Bandit
    line('4206964', 1), // Trap Hole
    line('54652250', 1), // Man-Eater Bug
    line('1435851', 2), // Dragon Treasure
    line('68005187', 1), // Soul Exchange
  ],
}

const MILL: AiRecipe = {
  id: 'mill',
  main: [
    line('72892473', 2), // Card Destruction
    line('41142615', 2), // The Cheerful Coffin
    line('66788016', 1), // Fissure
    line('30778711', 2), // Shadow Ghoul
    line('31786629', 2), // Thunder Dragon
    line('26202165', 2), // Sangan
    line('78010363', 2), // Witch
    line('31560081', 2), // Magician of Faith
    line('28933734', 2), // Mask of Darkness
    line('15025844', 2), // Mystical Elf
    line('13039848', 2), // Giant Soldier of Stone
    line('85639257', 2), // Aqua Madoor
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('19613556', 1), // Heavy Storm
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('77414722', 1), // Magic Jammer
    line('13945283', 2), // Wall of Illusion
    line('38199696', 1), // Red Medicine
    line('40640057', 1), // Kuriboh
    line('54652250', 1), // Man-Eater Bug
  ],
}

const TOOLBOX: AiRecipe = {
  id: 'toolbox',
  main: [
    line('31560081', 3), // Magician of Faith
    line('28933734', 2), // Mask of Darkness
    line('54652250', 2), // Man-Eater Bug
    line('7089711', 2), // Hane-Hane
    line('26202165', 2), // Sangan
    line('78010363', 2), // Witch
    line('3819470', 2), // Seven Tools of the Bandit
    line('97590747', 2), // La Jinn
    line('40640057', 1), // Kuriboh
    line('13945283', 2), // Wall of Illusion
    line('13039848', 2), // Giant Soldier of Stone
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('79759861', 1), // Tribute to the Doomed
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('77414722', 2), // Magic Jammer
    line('41420027', 1), // Solemn Judgment
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
  ],
}

/** 29 July 2002 tournament beatdown, without three copies of every boss. */
const BEAT: AiRecipe = {
  id: 'beat',
  main: [
    line('70781052', 2), // Summoned Skull
    line('36996508', 2), // Dark Magician
    line('89631139', 2), // Blue-Eyes White Dragon
    line('97590747', 3), // La Jinn
    line('23771716', 2), // 7 Colored Fish
    line('31560081', 3), // Magician of Faith
    line('28933734', 2), // Mask of Darkness
    line('78010363', 2), // Witch
    line('54652250', 2), // Man-Eater Bug
    line('55144522', 1), // Pot of Greed
    line('83764718', 1), // Monster Reborn
    line('53129443', 1), // Dark Hole
    line('12580477', 1), // Raigeki
    line('4031928', 1), // Change of Heart
    line('66788016', 2), // Fissure
    line('79759861', 2), // Tribute to the Doomed
    line('19613556', 1), // Heavy Storm
    line('68005187', 1), // Soul Exchange
    line('72892473', 1), // Card Destruction
    line('44095762', 1), // Mirror Force
    line('3819470', 1), // Seven Tools of the Bandit
    line('98069388', 1), // Horn of Heaven
    line('41420027', 1), // Solemn Judgment
    line('4206964', 1), // Trap Hole
    line('12607053', 1), // Waboku
    line('72302403', 1), // Swords of Revealing Light
    line('26202165', 1), // Sangan
  ],
}

const DARK: AiRecipe = {
  id: 'dark',
  theme: { attribute: 'DARK' },
  main: [
    line('70781052', 2), // Summoned Skull
    line('36996508', 2), // Dark Magician
    line('97590747', 3), // La Jinn
    line('26202165', 2), // Sangan
    line('31560081', 2), // Magician of Faith
    line('28933734', 2), // Mask of Darkness
    line('59197169', 2), // Yami
    line('37120512', 2), // Sword of Dark Destruction
    line('79759861', 1), // Tribute to the Doomed
    line('13945283', 2), // Wall of Illusion
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('77414722', 1), // Magic Jammer
    line('54652250', 2), // Man-Eater Bug
    line('24611934', 2), // Ryu-Kishin Powered
    line('41392891', 1), // Feral Imp
  ],
}

/** Tuned critter: Witch finds the beater, Sangan finds Faith, Kuriboh, or itself. */
const CRITOP: AiRecipe = {
  id: 'critop',
  main: [
    line('26202165', 2), // Sangan
    line('78010363', 2), // Witch of the Black Forest
    line('97590747', 3), // La Jinn
    line('31560081', 3), // Magician of Faith
    line('40640057', 1), // Kuriboh
    line('54652250', 1), // Man-Eater Bug
    line('28933734', 1), // Mask of Darkness
    line('7089711', 1), // Hane-Hane
    line('11384280', 1), // Cannon Soldier
    line('13945283', 1), // Wall of Illusion
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('79759861', 2), // Tribute to the Doomed
    line('72302403', 1), // Swords of Revealing Light
    line('85602018', 1), // Last Will
    line('44095762', 1), // Mirror Force
    line('41420027', 1), // Solemn Judgment
    line('77414722', 2), // Magic Jammer
    line('3819470', 2), // Seven Tools of the Bandit
    line('98069388', 1), // Horn of Heaven
    line('4206964', 1), // Trap Hole
    line('12607053', 2), // Waboku
    line('56830749', 1), // Share the Pain
    line('13039848', 1), // Giant Soldier of Stone
  ],
}

/** Muka Muka plus Thunder Dragon. Reinforcements, not Waboku, to step over the other Muka. */
const MUKARE: AiRecipe = {
  id: 'mukare',
  main: [
    line('46657337', 3), // Muka Muka
    line('31786629', 3), // Thunder Dragon
    line('97590747', 3), // La Jinn
    line('78010363', 2), // Witch
    line('26202165', 2), // Sangan
    line('31560081', 3), // Magician of Faith
    line('40640057', 1), // Kuriboh
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('79759861', 2), // Tribute to the Doomed
    line('77414722', 2), // Magic Jammer
    line('17814387', 3), // Reinforcements
    line('44095762', 1), // Mirror Force
    line('4206964', 1), // Trap Hole
    line('24094653', 2), // Polymerization
    line('3819470', 1), // Seven Tools of the Bandit
    line('41420027', 1), // Solemn Judgment
    line('72302403', 1), // Swords of Revealing Light
    line('54652250', 1), // Man-Eater Bug
  ],
}

/** The offensive Exodia NickWhiz1 kept in the side. */
const EXOBEAT: AiRecipe = {
  id: 'exobeat',
  main: [
    line('33396948', 1),
    line('8124921', 1),
    line('44519536', 1),
    line('70903634', 1),
    line('7902349', 1),
    line('97590747', 3), // La Jinn
    line('70781052', 2), // Summoned Skull
    line('26202165', 2), // Sangan
    line('78010363', 2), // Witch
    line('11384280', 1), // Cannon Soldier
    line('31560081', 2), // Magician of Faith
    line('85602018', 2), // Last Will
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('98495314', 2), // Sword of Deep-Seated
    line('79759861', 1), // Tribute to the Doomed
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('77414722', 1), // Magic Jammer
    line('72302403', 2), // Swords of Revealing Light
    line('68005187', 1), // Soul Exchange
    line('25955164', 1), // Sanga of the Thunder
    line('13039848', 1), // Giant Soldier of Stone
  ],
}

const SOULX: AiRecipe = {
  id: 'soulx',
  main: [
    line('70781052', 2), // Summoned Skull
    line('21417692', 1), // Dark Elf — Witch finds DEF 800
    line('94773007', 1), // Jirai Gumo — Witch finds DEF 100
    line('68005187', 2), // Soul Exchange
    line('56830749', 2), // Share the Pain
    line('79759861', 1), // Tribute to the Doomed
    line('78010363', 2), // Witch
    line('26202165', 2), // Sangan
    line('31560081', 3), // Magician of Faith
    line('10189126', 2), // Masked Sorcerer
    line('15150365', 1), // White Magical Hat
    line('54652250', 1), // Man-Eater Bug
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 3), // Fissure
    line('44095762', 1), // Mirror Force
    line('41420027', 3), // Solemn Judgment
    line('4206964', 1), // Trap Hole
    line('77414722', 1), // Magic Jammer
    line('98069388', 1), // Horn of Heaven
    line('97590747', 2), // La Jinn
    line('12607053', 1), // Waboku
    line('85602018', 1), // Last Will
  ],
}

const HATWILL: AiRecipe = {
  id: 'hatwill',
  main: [
    line('15150365', 3), // White Magical Hat — Sangan finds ATK 1000
    line('10189126', 2), // Masked Sorcerer
    line('88279736', 2), // Robbin' Goblin
    line('78010363', 2), // Witch
    line('26202165', 2), // Sangan
    line('97590747', 2), // La Jinn
    line('31560081', 3), // Magician of Faith
    line('40640057', 1), // Kuriboh
    line('85602018', 2), // Last Will
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('79759861', 2), // Tribute to the Doomed
    line('44095762', 1), // Mirror Force
    line('77414722', 2), // Magic Jammer
    line('3819470', 1), // Seven Tools of the Bandit
    line('41420027', 1), // Solemn Judgment
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('72302403', 1), // Swords of Revealing Light
    line('13945283', 1), // Wall of Illusion
    line('54652250', 1), // Man-Eater Bug
  ],
}

/** Six 1800s plus Reinforcements, the answer to Muka Muka. */
const FISHRE: AiRecipe = {
  id: 'fishre',
  main: [
    line('97590747', 3), // La Jinn
    line('23771716', 3), // 7 Colored Fish
    line('17814387', 3), // Reinforcements
    line('78010363', 2), // Witch
    line('26202165', 2), // Sangan
    line('31560081', 3), // Magician of Faith
    line('40640057', 1), // Kuriboh
    line('54652250', 1), // Man-Eater Bug
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('79759861', 2), // Tribute to the Doomed
    line('44095762', 1), // Mirror Force
    line('77414722', 2), // Magic Jammer
    line('3819470', 1), // Seven Tools of the Bandit
    line('41420027', 1), // Solemn Judgment
    line('4206964', 1), // Trap Hole
    line('72302403', 1), // Swords of Revealing Light
    line('12607053', 1), // Waboku
    line('13945283', 2), // Wall of Illusion
    line('85602018', 1), // Last Will
    line('13039848', 1), // Giant Soldier of Stone
  ],
}

const FLUTE: AiRecipe = {
  id: 'flute',
  main: [
    line('89631139', 3), // Blue-Eyes White Dragon
    line('17985575', 3), // Lord of D.
    line('43973174', 3), // The Flute of Summoning Dragon
    line('74677422', 2), // Red-Eyes Black Dragon
    line('88819587', 1), // Baby Dragon
    line('50913601', 1), // Mountain
    line('26202165', 2), // Sangan
    line('78010363', 2), // Witch
    line('31560081', 2), // Magician of Faith
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('77414722', 2), // Magic Jammer
    line('3819470', 1), // Seven Tools of the Bandit
    line('41420027', 1), // Solemn Judgment
    line('12607053', 1), // Waboku
    line('4206964', 1), // Trap Hole
    line('1435851', 1), // Dragon Treasure
    line('68005187', 1), // Soul Exchange
    line('54652250', 1), // Man-Eater Bug
    line('85602018', 1), // Last Will
  ],
}

/** Last Will finds the monster of 1500 ATK or less the list is built around. */
const WILL: AiRecipe = {
  id: 'will',
  main: [
    line('85602018', 3), // Last Will
    line('79759861', 1), // Tribute to the Doomed
    line('26202165', 2), // Sangan
    line('78010363', 2), // Witch
    line('46657337', 2), // Muka Muka
    line('11384280', 2), // Cannon Soldier
    line('31560081', 2), // Magician of Faith
    line('40640057', 2), // Kuriboh
    line('15150365', 2), // White Magical Hat
    line('10189126', 1), // Masked Sorcerer
    line('97590747', 2), // La Jinn
    line('80604091', 1), // Ultimate Offering
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('4031928', 1), // Change of Heart
    line('19613556', 1), // Heavy Storm
    line('66788016', 2), // Fissure
    line('44095762', 1), // Mirror Force
    line('77414722', 2), // Magic Jammer
    line('41420027', 1), // Solemn Judgment
    line('12607053', 2), // Waboku
    line('4206964', 1), // Trap Hole
    line('72302403', 1), // Swords of Revealing Light
    line('13039848', 1), // Giant Soldier of Stone
    line('13945283', 1), // Wall of Illusion
  ],
}

/** Walls plus Robbin' Goblin. The list Muka Muka pushed out of the top. */
const GOBLIN: AiRecipe = {
  id: 'goblin',
  main: [
    line('88279736', 3), // Robbin' Goblin
    line('13039848', 3), // Giant Soldier of Stone
    line('15025844', 2), // Mystical Elf
    line('85639257', 2), // Aqua Madoor
    line('13945283', 2), // Wall of Illusion
    line('51275027', 2), // The Unhappy Maiden
    line('26202165', 2), // Sangan
    line('78010363', 2), // Witch
    line('31560081', 2), // Magician of Faith
    line('40640057', 1), // Kuriboh
    line('54652250', 1), // Man-Eater Bug
    line('55144522', 1), // Pot of Greed
    line('12580477', 1), // Raigeki
    line('53129443', 1), // Dark Hole
    line('83764718', 1), // Monster Reborn
    line('19613556', 1), // Heavy Storm
    line('66788016', 1), // Fissure
    line('72302403', 2), // Swords of Revealing Light
    line('44095762', 1), // Mirror Force
    line('12607053', 2), // Waboku
    line('44209392', 3), // Castle Walls
    line('77414722', 1), // Magic Jammer
    line('3819470', 1), // Seven Tools of the Bandit
    line('4206964', 1), // Trap Hole
    line('38199696', 1), // Red Medicine
  ],
}

const SUBSTITUTES: Record<AiRecipeId, RecipeLine[]> = {
  walls: SUB_GENERIC,
  burn: SUB_GENERIC,
  weaks: SUB_GENERIC,
  flips: SUB_GENERIC,
  ox: SUB_GENERIC,
  heal: SUB_GENERIC,
  swarm: SUB_GENERIC,
  fire: SUB_FIRE,
  harpie: SUB_GENERIC,
  moth: SUB_GENERIC,
  yugi: SUB_SPELLCASTER,
  gaia: SUB_GENERIC,
  beast: SUB_BEAST,
  water: SUB_WATER,
  wind: SUB_WIND,
  zombie: SUB_ZOMBIE,
  guard: SUB_GENERIC,
  machine: SUB_MACHINE,
  gate: SUB_GENERIC,
  direct: SUB_GENERIC,
  fish: SUB_GENERIC,
  skull: SUB_GENERIC,
  dragon: SUB_GENERIC,
  stallburn: SUB_GENERIC,
  hats: SUB_GENERIC,
  coins: SUB_GENERIC,
  turtle: SUB_GENERIC,
  fiend: SUB_FIEND,
  warrior: SUB_WARRIOR,
  earth: SUB_EARTH,
  critter: SUB_GENERIC,
  exodia: SUB_GENERIC,
  muka: SUB_GENERIC,
  soul: SUB_GENERIC,
  fusion: SUB_GENERIC,
  lord: SUB_GENERIC,
  mill: SUB_GENERIC,
  toolbox: SUB_GENERIC,
  beat: SUB_GENERIC,
  dark: SUB_DARK,
  critop: SUB_GENERIC,
  mukare: SUB_GENERIC,
  exobeat: SUB_GENERIC,
  soulx: SUB_GENERIC,
  hatwill: SUB_GENERIC,
  fishre: SUB_GENERIC,
  flute: SUB_GENERIC,
  will: SUB_GENERIC,
  goblin: SUB_GENERIC,
}

const RECIPES: Record<AiRecipeId, AiRecipe> = {
  walls: WALLS,
  burn: BURN,
  weaks: WEAKS,
  flips: FLIPS,
  ox: OX,
  heal: HEAL,
  swarm: SWARM,
  fire: FIRE,
  harpie: HARPIE,
  moth: MOTH,
  yugi: YUGI,
  gaia: GAIA,
  beast: BEAST,
  water: WATER,
  wind: WIND,
  zombie: ZOMBIE,
  guard: GUARD,
  machine: MACHINE,
  gate: GATE,
  direct: DIRECT,
  fish: FISH,
  skull: SKULL,
  dragon: DRAGON,
  stallburn: STALLBURN,
  hats: HATS,
  coins: COINS,
  turtle: TURTLE,
  fiend: FIEND,
  warrior: WARRIOR,
  earth: EARTH,
  critter: CRITTER,
  exodia: EXODIA,
  muka: MUKA,
  soul: SOUL,
  fusion: FUSION,
  lord: LORD,
  mill: MILL,
  toolbox: TOOLBOX,
  beat: BEAT,
  dark: DARK,
  critop: CRITOP,
  mukare: MUKARE,
  exobeat: EXOBEAT,
  soulx: SOULX,
  hatwill: HATWILL,
  fishre: FISHRE,
  flute: FLUTE,
  will: WILL,
  goblin: GOBLIN,
}

for (const recipe of Object.values(RECIPES)) {
  recipe.substitutes = SUBSTITUTES[recipe.id]
}

export const EASY_POOL: AiRecipeId[] = [
  'walls',
  'burn',
  'weaks',
  'flips',
  'ox',
  'heal',
  'swarm',
  'fire',
  'harpie',
  'moth',
]
export const NORMAL_POOL: AiRecipeId[] = [
  'gaia',
  'yugi',
  'beast',
  'water',
  'wind',
  'zombie',
  'guard',
  'machine',
  'gate',
  'direct',
]
export const HARD_POOL: AiRecipeId[] = [
  'fish',
  'skull',
  'dragon',
  'stallburn',
  'hats',
  'coins',
  'turtle',
  'fiend',
  'warrior',
  'earth',
]
export const EXPERT_POOL: AiRecipeId[] = [
  'critter',
  'exodia',
  'muka',
  'soul',
  'fusion',
  'lord',
  'mill',
  'toolbox',
  'beat',
  'dark',
]
export const MASTER_POOL: AiRecipeId[] = [
  'critop',
  'mukare',
  'exodia',
  'exobeat',
  'soulx',
  'hatwill',
  'fishre',
  'flute',
  'will',
  'goblin',
]

const lastPicked = new Map<AiDifficulty, AiRecipeId>()

/**
 * Random published list for this CPU. Avoids repeating the previous pick
 * for the same difficulty when the pool has more than one recipe.
 */
export function recipeIdForDifficulty(difficulty: AiDifficulty): AiRecipeId {
  const pool = poolForDifficulty(difficulty)
  const picked = pickAvoidingLast(pool, lastPicked.get(difficulty))
  lastPicked.set(difficulty, picked)
  return picked
}

export function poolForDifficulty(difficulty: AiDifficulty): AiRecipeId[] {
  if (difficulty === 'easy') return EASY_POOL
  if (difficulty === 'normal') return NORMAL_POOL
  if (difficulty === 'hard') return HARD_POOL
  if (difficulty === 'expert') return EXPERT_POOL
  return MASTER_POOL
}

export function recipeById(id: AiRecipeId): AiRecipe {
  return RECIPES[id]
}

export function allRecipeIds(): AiRecipeId[] {
  return Object.keys(RECIPES) as AiRecipeId[]
}

export function appliesEraLimits(difficulty: AiDifficulty): boolean {
  return (
    difficulty === 'easy' ||
    difficulty === 'normal' ||
    difficulty === 'hard'
  )
}

/** True while the house published a temporary copy list. */
export { houseBanlistActive }

/**
 * Copies of one card a CPU list may run: always-on caps, the July 2002
 * list on Easy / Normal / Hard, and the house banlist on every difficulty,
 * including Master, when that list is active.
 */
export function cpuCopyCap(id: string, difficulty: AiDifficulty | 'test'): number {
  let cap = CPU_COPY_CAPS[id] ?? 3
  if (difficulty !== 'test' && appliesEraLimits(difficulty)) {
    cap = Math.min(cap, ERA_LIMITS[id] ?? 3)
  }
  if (houseBanlistActive()) {
    const house = houseLimits()[id]
    if (house !== undefined) cap = Math.min(cap, house)
  }
  return cap
}

/**
 * Recipe lines after the July 2002 limited list (Easy / Normal / Hard),
 * the house banlist on every difficulty including Master, the always-on
 * CPU copy caps, and substitutes filling any cut copies back to 40.
 * Trap Hole stays on the always-on cap.
 */
export function expandRecipeMain(
  recipe: AiRecipe,
  difficulty: AiDifficulty,
): RecipeLine[] {
  const out: RecipeLine[] = []
  const counts = new Map<string, number>()

  const add = (id: string, n: number): void => {
    if (n <= 0) return
    const have = counts.get(id) ?? 0
    const take = Math.min(n, cpuCopyCap(id, difficulty) - have)
    if (take <= 0) return
    counts.set(id, have + take)
    const existing = out.find((line) => line.id === id)
    if (existing) existing.copies += take
    else out.push({ id, copies: take })
  }

  for (const line of recipe.main) add(line.id, line.copies)
  for (const line of recipe.substitutes ?? []) {
    const total = [...counts.values()].reduce((sum, n) => sum + n, 0)
    if (total >= 40) break
    add(line.id, Math.min(line.copies, 40 - total))
  }
  return out
}

export function recipeHasPolymerization(recipe: AiRecipe): boolean {
  return recipe.main.some(
    (line) => line.id === POLYMERIZATION_ID && line.copies > 0,
  )
}

function pickAvoidingLast(
  pool: AiRecipeId[],
  last: AiRecipeId | undefined,
): AiRecipeId {
  const choices =
    last && pool.length > 1 ? pool.filter((id) => id !== last) : pool
  return choices[Math.floor(Math.random() * choices.length)]!
}
